import { Navigate, useParams, Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import { PageHero, useTitle } from "../components/PageHero";
import { Reveal, Tag } from "../components/Reveal";
import { PrimaryBtn } from "../components/ui";
import { CTABand } from "../components/CTABand";
import { SOLUTIONS } from "../data/solutions";

export default function SolutionDetail() {
  const { slug } = useParams();
  const s = SOLUTIONS.find((x) => x.slug === slug);
  useTitle(s ? `${s.title} | SABAKT Saudi Arabia` : "SABAKT");
  if (!s) return <Navigate to="/solutions" replace />;

  const others = SOLUTIONS.filter((x) => x.slug !== slug).slice(0, 4);

  return (
    <main>
      <PageHero image={s.image} eyebrow={`Solution · ${s.seo}`} testid="solution-detail-hero" title={s.heroLine} accent={s.heroLine.split(" ").pop()}>
        <div className="mt-8 flex flex-wrap gap-4">
          <PrimaryBtn to="/rfq" testid="solution-rfq-btn">Request a Technical Quote</PrimaryBtn>
        </div>
      </PageHero>

      <section className="border-b border-seam py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-8 lg:grid-cols-[1.2fr_1fr] lg:px-16">
          <Reveal>
            <Tag>Overview</Tag>
            <p className="mt-4 text-lg leading-relaxed text-frost/90">{s.intro}</p>
            <p className="mt-6 text-base leading-relaxed text-cadmium">{s.value}</p>
            {s.featured && (
              <div className="mt-10 border border-spark/50 bg-iron p-8" data-testid="featured-service">
                <Tag>High-Conversion Service</Tag>
                <h3 className="mt-3 font-display text-2xl font-extrabold uppercase text-frost">{s.featured.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-cadmium">{s.featured.text}</p>
                <div className="mt-6">
                  <PrimaryBtn to="/rfq" testid="featured-service-btn">{s.featured.cta}</PrimaryBtn>
                </div>
              </div>
            )}
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border border-seam bg-iron">
              <div className="border-b border-seam px-7 py-4">
                <Tag>Scope of Capability</Tag>
              </div>
              <ul className="divide-y divide-seam/60">
                {s.capabilities.map((c) => (
                  <li key={c} className="flex items-start gap-3 px-7 py-4 text-sm text-frost/85">
                    <Check size={16} className="mt-0.5 shrink-0 text-spark" /> {c}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-iron py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Tag>More Solutions</Tag>
          <div className="mt-6 grid gap-px border border-seam bg-seam sm:grid-cols-2 lg:grid-cols-4">
            {others.map((o) => (
              <Link key={o.slug} to={`/solutions/${o.slug}`} data-testid={`related-solution-${o.slug}`}
                className="group flex items-center justify-between gap-3 bg-obsidian px-6 py-5 hover:bg-steel">
                <span className="font-display text-base font-bold uppercase tracking-wide text-frost/90 group-hover:text-spark">{o.title}</span>
                <ArrowUpRight size={16} className="shrink-0 text-cadmium group-hover:text-spark" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </main>
  );
}
